
import React from "react";
import { Badge } from "@/components/ui/badge";
import { 
  Card, 
  CardHeader, 
  CardContent 
} from "@/components/ui/card";
import { Trophy, Lock, CheckCircle2 } from "lucide-react";

interface Achievement {
  id: string;
  title: string;
  description: string;
  pointsRequired: number;
  icon: string;
  category: 'engagement' | 'milestone' | 'special';
  unlocked: boolean;
}

interface KarmaAchievementsProps {
  karmaPoints: number;
}

const KarmaAchievements: React.FC<KarmaAchievementsProps> = ({ karmaPoints }) => {
  // List of available achievements
  const achievements: Achievement[] = [
    {
      id: "first-login",
      title: "First Steps",
      description: "Join the community and make your first login",
      pointsRequired: 0,
      icon: "🏆",
      category: 'milestone',
      unlocked: true
    },
    {
      id: "karma-50",
      title: "Rising Star",
      description: "Reach 50 karma points",
      pointsRequired: 50,
      icon: "⭐",
      category: 'milestone',
      unlocked: karmaPoints >= 50
    },
    {
      id: "karma-100",
      title: "Community Friend",
      description: "Reach 100 karma points",
      pointsRequired: 100,
      icon: "🌟",
      category: 'milestone',
      unlocked: karmaPoints >= 100
    },
    {
      id: "karma-250",
      title: "Knowledge Seeker",
      description: "Reach 250 karma points",
      pointsRequired: 250,
      icon: "🔥",
      category: 'milestone',
      unlocked: karmaPoints >= 250
    },
    {
      id: "karma-500",
      title: "Mentor Material",
      description: "Reach 500 karma points",
      pointsRequired: 500,
      icon: "🚀",
      category: 'milestone',
      unlocked: karmaPoints >= 500
    },
    {
      id: "karma-1000",
      title: "Community Leader",
      description: "Reach 1000 karma points",
      pointsRequired: 1000,
      icon: "👑",
      category: 'milestone',
      unlocked: karmaPoints >= 1000
    },
  ];

  // Count unlocked achievements
  const unlockedCount = achievements.filter(a => a.unlocked).length;
  
  return (
    <Card className="overflow-hidden border shadow-sm">
      <CardHeader className="p-4 bg-muted/30 border-b space-y-0">
        <h3 className="font-medium flex items-center text-base">
          <Trophy className="h-4 w-4 mr-2 text-amber-500" />
          Achievements
          <Badge variant="secondary" className="ml-auto">
            {unlockedCount}/{achievements.length}
          </Badge>
        </h3>
      </CardHeader>
      
      <CardContent className="p-0">
        <div className="max-h-[280px] overflow-y-auto">
          <div className="grid grid-cols-1 divide-y">
            {achievements.map((achievement) => (
              <div key={achievement.id} className="p-3.5 flex items-center gap-3">
                <div className={`text-xl ${!achievement.unlocked ? "opacity-40" : ""}`}>
                  {achievement.icon}
                </div>
                <div className="flex-grow">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-sm">
                      {achievement.title}
                    </h4>
                    {achievement.unlocked ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    ) : (
                      <Lock className="h-3 w-3 opacity-60" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {achievement.description}
                  </p>
                </div>
                <Badge 
                  variant={achievement.unlocked ? "default" : "outline"} 
                  className={`ml-auto ${achievement.unlocked ? "bg-blue-500" : ""}`}
                >
                  {achievement.pointsRequired} pts
                </Badge>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default KarmaAchievements;
